<template>
  <div class="bg-secondary-dark h-full flex flex-col">
    <div class="flex-1 overflow-hidden">
      <div class="flex justify-end pr-2 pt-2">
        <button
          type="button"
          class="text-secondary-lighter hover:text-common-white hover:bg-secondary rounded-sm p-1 cursor-pointer transition-colors"
          :aria-label="sidebarCollapsed ? 'Seitenleiste ausklappen' : 'Seitenleiste einklappen'"
          @click="handleSidebarToggleClick"
        >
          <BIconChevronDoubleRight v-if="sidebarCollapsed" class="w-5 h-5" />
          <BIconChevronDoubleLeft v-else class="w-5 h-5" />
        </button>
      </div>

      <div class="flex items-center px-4 pt-3 pb-6 w-auto overflow-hidden">
        <div
          class="flex overflow-hidden"
          :class="[sidebarCollapsed ? '' : 'gap-3']"
        >
          <ServiceproviderLogo :small-icon="sidebarCollapsed" class="mx-auto" />

          <div class="flex flex-col my-auto">
            <p
              v-if="!sidebarCollapsed"
              class="text-common-white font-bold  truncate mt-1"
              :class="{ 'cursor-pointer': isAdminUser }"
              :title="isAdminUser ? `${serviceproviderName} (Klicken, um die UUID zu kopieren)` : undefined"
              @click="copyServiceproviderUUID"
            >
              {{ serviceproviderName }}
            </p>
          </div>
        </div>
      </div>

      <div class="flex grow flex-col">
        <nav class="flex-1 space-y-2 px-2 justify-center" aria-label="Sidebar">
          <div v-for="n in nav" :key="n.to">
            <NuxtLink
              v-if="!n.submenus || n.submenus.length === 0"
              :data-tour="n.tour"
              :to="n.to"
              class="group flex justify-between py-2 px-2 text-md"
            >
              <component
                :is="n.icon"
                :class="[
                  n.isActive
                    ? 'text-lilac'
                    : 'text-secondary-lighter group-hover:text-common-white',
                  sidebarCollapsed ? 'my-auto mx-auto' : 'mr-3',
                  'w-5 h-5 self-center',
                ]"
              />

              <span
                v-if="!sidebarCollapsed"
                :class="[
                  n.isActive
                    ? 'text-lilac font-bold'
                    : 'text-secondary-lighter group-hover:text-common-white',
                  'flex-1 ',
                ]"
              >{{ n.title }}</span>

              <span v-if="n.alerts > 0 && !sidebarCollapsed" class="bg-secondary-highlight-light text-common-white text-xs font-bold ml-2 px-2.5 py-1 rounded-full my-auto"> {{ n?.alerts }}</span>
            </NuxtLink>

            <div v-else>
              <div
                class="group flex justify-between py-2 px-2 text-md cursor-pointer"
                @click="handleNavClick(n, $event)"
              >
                <component
                  :is="n.icon"
                  :class="[
                    n.isActive
                      ? 'text-lilac'
                      : 'text-secondary-lighter group-hover:text-common-white',
                    sidebarCollapsed ? 'my-auto mx-auto' : 'mr-3',
                    'w-5 h-5 self-center',
                  ]"
                />

                <span
                  v-if="!sidebarCollapsed"
                  :class="[
                    n.isActive
                      ? 'text-lilac font-bold'
                      : 'text-secondary-lighter group-hover:text-common-white',
                    'flex-1 ',
                  ]"
                >{{ n.title }}</span>

                <span v-if="getSubmenuAlerts(n) > 0 && !sidebarCollapsed && closedSubMenus.has(n.to)" class="bg-secondary-highlight-light text-common-white text-xs font-bold ml-2 px-2.5 py-1 rounded-full my-auto"> {{ getSubmenuAlerts(n) }}</span>

                <component
                  :is="!closedSubMenus.has(n.to) ? BIconChevronDown : BIconChevronUp"
                  v-if="!sidebarCollapsed"
                  :class="[
                    n.isActive
                      ? 'text-lilac'
                      : 'text-secondary-lighter group-hover:text-common-white',
                    'w-4 h-4 self-center ml-2',
                  ]"
                  @click.stop="toggleSubmenu(n)"
                />
              </div>

              <div v-if="!closedSubMenus.has(n.to) && !sidebarCollapsed" class="ml-8">
                <NuxtLink
                  v-for="submenu in n.submenus"
                  :key="submenu.to"
                  :to="submenu.to"
                  class="group flex justify-between py-1 px-2 text-sm"
                >
                  <span class="flex items-center min-w-0">
                    <span
                      :class="[
                        route.path === submenu.to
                          ? 'text-lilac font-bold'
                          : 'text-secondary-lighter group-hover:text-common-white',
                      ]"
                    >{{ submenu.title }}</span>
                  </span>
                  <span v-if="submenu.alerts > 0" class="bg-secondary-highlight-light text-common-white text-xs font-bold ml-2 px-2 py-0.5 rounded-full">{{ submenu.alerts }}</span>
                </NuxtLink>
              </div>
            </div>
          </div>
        </nav>
      </div>
    </div>

    <div v-if="isAdminUser && serviceprovider.reseller?.isCreatedByReseller && resellerName" class="flex items-center px-4 py-3">
      <span class="text-xs rounded-md text-secondary-lighter font-medium">
        Reseller: {{ resellerName }}
      </span>
    </div>
  </div>
</template>

<script setup>
import {
  BIconChevronDoubleLeft,
  BIconChevronDoubleRight,
  BIconActivity,
  BIconGear,
  BIconCardChecklist,
  BIconClipboardData,
  BIconChevronDown,
  BIconChevronUp
} from 'bootstrap-icons-vue'

import ServiceproviderLogo from '~/components/serviceprovider/ServiceproviderLogo.vue'
import { useCurrentServiceprovider } from '~/composables/useCurrentServiceprovider'
const route = useRoute()
const { userOrganization, isAdminUser, currentEnv } = useCurrentUserSession()
const { serviceprovider } = useCurrentServiceprovider()
const { $api, $logger, $toast } = useNuxtApp()

// Start expanded on every screen size so the submenus stay reachable; collapsing
// is a manual choice via the toggle above.
const sidebarCollapsed = ref(false)
const alerts = ref({
  dashboard: 0,
  changeHistory: 0,
  ioe: 0,
  ioa: 0,
  settings: 0
})

const closedSubMenus = ref(new Set())
const isSubmenusInitialized = ref(false)

const serviceproviderName = computed(() => serviceprovider.value.name)
const resellerName = ref('')

async function copyServiceproviderUUID () {
  if (!isAdminUser || !serviceprovider.value.uuid) {
    return
  }
  try {
    await navigator.clipboard.writeText(serviceprovider.value.uuid)
    $toast.success('Dienstleister-UUID kopiert')
  } catch (e) {
    $toast.error('Fehler beim Kopieren', e?.message)
    $logger.error(e)
  }
}

const nav = computed(() => {
  let uuid = userOrganization
  if (isAdminUser) {
    uuid = serviceprovider.value.uuid

    // Fetch reseller name if serviceprovider is created by a reseller and user is admin
    if (serviceprovider.value.reseller?.isCreatedByReseller && serviceprovider.value.reseller?.id) {
      $api.admin.getResellerInfo(serviceprovider.value.reseller.id).then((res) => {
        resellerName.value = res.reseller.name
      }).catch((error) => {
        $logger.error('Failed to fetch reseller name:', error)
      })
    }
  }

  return [
    {
      title: 'Monitoring',
      to: `/provider/${uuid}/monitoring`,
      tour: 'nav-monitoring',
      icon: BIconActivity,
      isActive: route.path.startsWith(`/provider/${uuid}/monitoring`),
      alerts: alerts.value.changeHistory,
      submenus: []
    },
    {
      title: 'Audit',
      to: `/provider/${uuid}/audit`,
      tour: 'nav-audit',
      icon: BIconClipboardData,
      isActive: route.path.startsWith(`/provider/${uuid}/audit`),
      alerts: alerts.value.ioe,
      submenus: []
    },
    {
      title: 'Quickcheck',
      to: `/provider/${uuid}/quickcheck`,
      tour: 'nav-quickcheck',
      icon: BIconCardChecklist,
      isActive: route.path.startsWith(`/provider/${uuid}/quickcheck`),
      alerts: alerts.value.settings,
      submenus: []
    },
    {
      title: 'Einstellungen',
      to: `/provider/${uuid}/settings`,
      icon: BIconGear,
      isActive: route.path.startsWith(`/provider/${uuid}/settings`),
      alerts: alerts.value.settings,
      submenus: [
        { title: 'Allgemein', to: `/provider/${uuid}/settings` },
        { title: 'Quickcheck', to: `/provider/${uuid}/settings/quickcheck` },
        ...(!currentEnv.isSelfhosted && !currentEnv.isDemoEnv && !(serviceprovider.value.reseller?.isCreatedByReseller && serviceprovider.value.reseller?.resellerInvoiced) ? [{ title: 'Preise & Zahlungen', to: `/provider/${uuid}/settings/billing` }] : []),
        { title: 'API', to: `/provider/${uuid}/settings/api` },
        { title: 'MCP', to: `/provider/${uuid}/settings/mcp` }
      ]
    }
  ]
})

function handleSidebarToggleClick () {
  sidebarCollapsed.value = !sidebarCollapsed.value
}

function handleNavClick (navItem, event) {
  if (navItem.submenus && navItem.submenus.length > 0) {
    event.preventDefault()
    navigateTo(navItem.submenus[0].to)
    if (closedSubMenus.value.has(navItem.to)) {
      closedSubMenus.value.delete(navItem.to)
    } else {
      closedSubMenus.value.add(navItem.to)
    }
  }
}

function toggleSubmenu (navItem) {
  if (closedSubMenus.value.has(navItem.to)) {
    closedSubMenus.value.delete(navItem.to)
  } else {
    closedSubMenus.value.add(navItem.to)
  }
}

function getSubmenuAlerts (navItem) {
  if (!navItem.submenus || navItem.submenus.length === 0) { return 0 }
  return navItem.submenus.reduce((sum, submenu) => sum + (submenu.alerts || 0), 0)
}

watch(nav, (newNav) => {
  if (!isSubmenusInitialized.value && newNav.length > 0) {
    newNav.forEach((navItem) => {
      // collapsed by default, but not while one of its pages is open - its entry should stay visible
      if (navItem.submenus && navItem.submenus.length > 0 && navItem.to.includes('/settings') && !navItem.isActive) {
        closedSubMenus.value.add(navItem.to)
      }
    })
    isSubmenusInitialized.value = true
  }
}, { immediate: true })

</script>

<style scoped></style>
